import { ImgLogo } from "@/assets/images"
import {
  isMaintenancePage,
  useResolvedMaintenanceMode,
} from "@/hooks/use-maintenance-mode"
import { useLocation } from "react-router-dom"

interface MaintenanceRouteProps {
  children: React.ReactNode
}

/**
 * Route guard component that redirects to maintenance page if maintenance mode is enabled
 * This acts as middleware at the router level to prevent access via direct URL
 */
export function MaintenanceRoute({ children }: MaintenanceRouteProps) {
  const { isMaintenanceMode, isResolvingMaintenanceMode } =
    useResolvedMaintenanceMode()
  const location = useLocation()
  const isCurrentPageMaintenancePage = isMaintenancePage(location.pathname)

  // Wait for static config before rendering protected content to avoid a flash
  // of the app or maintenance page while the API value is being resolved.
  if (isResolvingMaintenanceMode) {
    return <MaintenanceModeLoading />
  }

  // useMaintenanceMode performs the actual navigation. Keep the current route
  // blank for a frame so protected content or the maintenance page does not
  // flash while the centralized navigation effect runs.
  const isWaitingForMaintenanceRedirect =
    isMaintenanceMode && !isCurrentPageMaintenancePage
  const isWaitingForMaintenanceExit =
    !isMaintenanceMode && isCurrentPageMaintenancePage

  if (isWaitingForMaintenanceRedirect || isWaitingForMaintenanceExit) {
    return null
  }

  return <>{children}</>
}

function MaintenanceModeLoading() {
  return (
    <div className="bg-background flex h-full w-full flex-col items-center justify-center gap-8">
      <div className="flex flex-col items-center gap-6">
        <img
          src={ImgLogo}
          alt=""
          className="h-10 w-auto opacity-90"
          aria-hidden
        />
        <div className="bg-border h-0.5 w-28 overflow-hidden rounded-full">
          <div className="bg-primary h-full w-10 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full" />
        </div>
      </div>
    </div>
  )
}
